import { useMemo, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useWizard } from "@/lib/wizard-context";
import { useTenant } from "@/lib/tenant-context";
import CardMockPreview from "@/components/CardMockPreview";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { ArrowLeft, ArrowRight, RotateCcw, Check, Ban } from "lucide-react";
import { cn } from "@/lib/utils";
import { CARD_COLORS } from "@/lib/mock-data";
import { cardBackgroundService } from "@/services";
import type { CardBackgroundRow } from "@/services/card-backgrounds";

const CardEditor = () => {
  const { tenant_slug } = useParams();
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const {
    config, setConfig, orientation, setOrientation,
    avatarPosition, setAvatarPosition,
    avatarScale, setAvatarScale,
    avatarVariants, selectedAvatarId,
    firstName, lastName,
    usePhotoOnly,
    cutoutUrls,
    useCutout, setUseCutout,
    backgroundId, setBackgroundId,
    backgroundUrl, setBackgroundUrl,
  } = useWizard();

  const selectedAvatar = avatarVariants.find((v) => v.id === selectedAvatarId);
  const availableColors = tenant?.cardColors ?? CARD_COLORS;
  const allowedOrientations = tenant?.allowedOrientations ?? ["landscape", "portrait"];

  // Backgrounds for this tenant (active only)
  const [backgrounds, setBackgrounds] = useState<CardBackgroundRow[]>([]);
  useEffect(() => {
    if (!tenant?.id) return;
    let cancelled = false;
    cardBackgroundService.listBackgrounds(tenant.id, true).then(({ data }) => {
      if (cancelled) return;
      setBackgrounds((data as CardBackgroundRow[]) || []);
    });
    return () => { cancelled = true; };
  }, [tenant?.id]);

  // If the previously-selected background isn't in this tenant's active set, clear it
  useEffect(() => {
    if (backgroundId && backgrounds.length > 0 && !backgrounds.find((b) => b.id === backgroundId)) {
      setBackgroundId(null);
      setBackgroundUrl(null);
    }
  }, [backgrounds, backgroundId, setBackgroundId, setBackgroundUrl]);

  // Auto-select first color if none chosen
  useEffect(() => {
    if (!config.color && availableColors.length > 0) {
      setConfig((prev) => ({ ...prev, color: availableColors[0] }));
    }
  }, [availableColors, config.color, setConfig]);

  const cardColor = config.color || availableColors[0] || CARD_COLORS[0];

  // Default orientation to first allowed if current isn't allowed
  useEffect(() => {
    if (allowedOrientations.length > 0 && !allowedOrientations.includes(orientation)) {
      setOrientation(allowedOrientations[0] as "landscape" | "portrait");
    }
  }, [allowedOrientations, orientation, setOrientation]);

  // Cutout is now the default — always true
  useEffect(() => {
    setUseCutout(true);
  }, [setUseCutout]);

  // For photo-only mode, create an object URL from the selfie file
  const photoUrl = useMemo(() => {
    if (usePhotoOnly && config.selfieFile) {
      return URL.createObjectURL(config.selfieFile);
    }
    return null;
  }, [usePhotoOnly, config.selfieFile]);

  const originalUrl = usePhotoOnly ? photoUrl : selectedAvatar?.imageUrl;
  const avatarKey = selectedAvatarId || "photo";
  const cutoutUrl = cutoutUrls[avatarKey] || null;

  // Use cutout if available, otherwise original
  const imageUrl = cutoutUrl || originalUrl;
  const isCutout = !!cutoutUrl;

  const isLandscape = orientation === "landscape";

  const pickBackground = (bg: CardBackgroundRow | null) => {
    setBackgroundId(bg?.id ?? null);
    setBackgroundUrl(bg?.image_url ?? null);
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="border-b px-4 py-3 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/t/${tenant_slug}/configure`, { state: { returnToStep: usePhotoOnly ? 2 : 4 } })}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <span className="font-display font-semibold text-sm">Card Editor</span>
      </div>

      <div className="flex-1 flex flex-col items-center gap-4 px-4 py-4 pb-28">
        {/* Orientation toggle */}
        {(() => {
          const allowed = tenant?.allowedOrientations ?? ["landscape", "portrait"];
          return allowed.length > 1 ? (
            <div className="flex gap-2 rounded-lg bg-muted p-1">
              {(["landscape", "portrait"] as const).filter((o) => allowed.includes(o)).map((o) => (
                <button
                  key={o}
                  className={cn(
                    "px-4 py-2 rounded-md text-sm font-medium transition-all capitalize",
                    orientation === o ? "bg-card shadow-card text-foreground" : "text-muted-foreground"
                  )}
                  onClick={() => setOrientation(o)}
                >
                  {o}
                </button>
              ))}
            </div>
          ) : null;
        })()}

        {/* Card preview + vertical color picker side by side */}
        <div className={cn(
          "w-full flex items-center gap-4",
          isLandscape ? "max-w-md" : "max-w-[260px]"
        )}>
          <div className="flex-1 min-w-0">
            <CardMockPreview
              cardColorHsl={cardColor.hsl}
              orientation={orientation}
              cardLayout={tenant?.cardLayout}
              avatarUrl={imageUrl || undefined}
              isCutout={isCutout || usePhotoOnly}
              avatarPosition={avatarPosition}
              avatarScale={avatarScale}
              firstName={firstName}
              lastName={lastName}
              tenantName={tenant?.brandName || tenant?.name}
              cardNetwork={tenant?.cardNetwork} showChip={tenant?.showChip} showCardNetwork={tenant?.showCardNetwork}
              backgroundUrl={backgroundUrl}
              interactive
              onAvatarDrag={setAvatarPosition}
              onAvatarZoom={setAvatarScale}
            />
          </div>

          {/* Vertical color picker */}
          <div className="flex flex-col gap-2 shrink-0">
            {availableColors.map((color) => (
              <button
                key={color.id}
                title={color.name}
                className={cn(
                  "h-9 w-9 rounded-full border-2 transition-all",
                  cardColor.id === color.id
                    ? "border-primary ring-2 ring-primary/30 scale-110"
                    : "border-transparent hover:border-muted-foreground/40"
                )}
                style={{ backgroundColor: `hsl(${color.hsl})` }}
                onClick={() => setConfig((prev) => ({ ...prev, color }))}
              >
                {cardColor.id === color.id && (
                  <Check className="h-3.5 w-3.5 text-white drop-shadow mx-auto" />
                )}
              </button>
            ))}
          </div>
        </div>

        <p className="text-xs text-muted-foreground text-center">
          Drag to reposition · Scroll/pinch to zoom · Pick a color
        </p>

        {/* Background picker — only shown if the tenant has at least one active background */}
        {backgrounds.length > 0 && (
          <div className="w-full max-w-md space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Background</span>
              <span className="text-xs text-muted-foreground">Sits behind your avatar</span>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
              {/* No background tile (always first) */}
              <button
                type="button"
                onClick={() => pickBackground(null)}
                className={cn(
                  "shrink-0 w-20 rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1 p-2 transition-colors",
                  backgroundId === null ? "border-primary text-primary" : "border-border text-muted-foreground hover:border-muted-foreground/60"
                )}
                style={{ aspectRatio: "86 / 54" }}
              >
                <Ban className="h-4 w-4" />
                <span className="text-[10px] font-medium leading-tight text-center">No background</span>
              </button>
              {backgrounds.map((bg) => {
                const selected = backgroundId === bg.id;
                return (
                  <button
                    key={bg.id}
                    type="button"
                    onClick={() => pickBackground(bg)}
                    className={cn(
                      "shrink-0 w-20 rounded-lg overflow-hidden border-2 transition-all relative",
                      selected ? "border-primary ring-2 ring-primary/30" : "border-border hover:border-muted-foreground/60"
                    )}
                    style={{ aspectRatio: "86 / 54" }}
                    title={bg.name}
                  >
                    <img src={bg.image_url} alt={bg.name} className="absolute inset-0 h-full w-full object-cover" draggable={false} />
                    {selected && (
                      <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground">
                        <Check className="h-3 w-3" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Zoom slider */}
        <div className="w-full max-w-sm space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{usePhotoOnly ? "Photo Size" : "Avatar Size"}</span>
            <span className="font-mono text-xs">{Math.round(avatarScale * 100)}%</span>
          </div>
          <Slider
            value={[avatarScale]}
            onValueChange={([v]) => setAvatarScale(v)}
            min={0.3}
            max={3}
            step={0.05}
          />
        </div>

        {/* Reset */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => { setAvatarPosition({ x: 50, y: 50 }); setAvatarScale(1); }}
          className="gap-2"
        >
          <RotateCcw className="h-3 w-3" /> Reset Position
        </Button>
      </div>

      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[960px] bg-background/80 backdrop-blur border-t p-4">
        <div className="max-w-lg mx-auto flex gap-3">
          <Button variant="outline" onClick={() => navigate(`/t/${tenant_slug}/configure`)} className="flex-1">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <Button
            className="flex-1 gradient-primary text-primary-foreground font-semibold gap-2"
            onClick={() => navigate(`/t/${tenant_slug}/submit`)}
          >
            Continue <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CardEditor;
